import { Injectable, inject } from '@angular/core';
import { AnimationOptions, SetOverlaysWebViewOptions, StatusBar, Style } from '@capacitor/status-bar';
import { PlatformDetectionService } from '@libs/util/shared';
import { ColorCalculationsService, ColorStyle } from '@libs/core/color';

@Injectable()
export class StatusBarService {
  private platformService = inject(PlatformDetectionService);
  private colorCalculationService = inject(ColorCalculationsService);

  /**
   * Set the current style of the status bar.
   */
  setStyle(style: Style): Promise<void> {
    return StatusBar.setStyle({ style });
  }

  /**
   * Set the background color of the status bar.
   *
   * This method is only supported on Android.
   */
  async setBackgroundColor(color: string, autoAdjustStyle = true): Promise<void> {
    if (autoAdjustStyle) {
      const style = this.colorCalculationService.getStyleFromBackground(color) === ColorStyle.Dark ? Style.Light : Style.Dark;
      await this.setStyle(style);
    }

    if (this.platformService.isAndroid()) {
      return await StatusBar.setBackgroundColor({ color });
    }

    return Promise.resolve();
  }

  /**
   * Show the status bar.
   */
  show(options?: AnimationOptions): Promise<void> {
    return StatusBar.show(options);
  }

  /**
   * Hide the status bar.
   */
  hide(options?: AnimationOptions): Promise<void> {
    return StatusBar.hide(options);
  }

  /**
   * Set whether the status bar should overlay the webview to allow usage
   * of the space underneath it.
   *
   * This method is only supported on Android.
   */
  setOverlaysWebView(options: SetOverlaysWebViewOptions): Promise<void> {
    if (this.platformService.isAndroid()) {
      return StatusBar.setOverlaysWebView(options);
    }

    return Promise.resolve();
  }
}
